import React, { useState, useEffect } from 'react';
import ProfileSection from './ProfileSection';
import '../styles/LandingPage.css';

const LandingPage = () => {
  const [entered, setEntered] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [textPhase, setTextPhase] = useState(0);
  const textPhrases = ['jxsh <3', 'click here'];
  const [typingComplete, setTypingComplete] = useState(false);

  // Setup background audio
  useEffect(() => {
    if (entered && audioEnabled) {
      // Check if audio element already exists
      let audio = document.querySelector('.background-audio');
      
      // If not, create it
      if (!audio) {
        audio = document.createElement('audio');
        audio.className = 'background-audio';
        audio.src = process.env.PUBLIC_URL + '/assets/song.mp3';
        audio.loop = true;
        audio.volume = 0.55; // Set initial volume to 55%
        audio.onended = () => {
          audio.play().catch(err => console.error('Error replaying audio:', err));
        };
        audio.onerror = () => {
          console.error('Audio error occurred');
          const errorEvent = new Event('audioError');
          window.dispatchEvent(errorEvent);
        };
        document.body.appendChild(audio);
      }
      
      // Try playing the audio
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        const errorEvent = new Event('audioError');
        window.dispatchEvent(errorEvent);
      });
    }
  }, [entered, audioEnabled]);
  
  // More complex typing animation effect
  useEffect(() => {
    if (entered) return;
    
    const currentPhrase = textPhrases[textPhase];
    
    const typingSpeed = isDeleting ? 100 : 150; // Delete faster than type
    const pauseDuration = 1000; // Pause before deleting
    
    let timer;
    
    // If the typed text is complete (matches the current phrase)
    if (!isDeleting && typedText === currentPhrase) {
      // If we're on the final phrase, just keep it displayed
      if (textPhase === textPhrases.length - 1) {
        setTypingComplete(true);
        return;
      }
      
      // Otherwise, set a timer to start deleting
      timer = setTimeout(() => {
        setIsDeleting(true);
      }, pauseDuration);
      
    // If we've deleted everything
    } else if (isDeleting && typedText === '') {
      setIsDeleting(false);
      setTextPhase((prev) => (prev + 1) % textPhrases.length);
      
    // Regular typing/deleting
    } else {
      timer = setTimeout(() => {
        setTypedText(prev => {
          if (isDeleting) {
            return prev.substring(0, prev.length - 1);
          } else {
            return currentPhrase.substring(0, prev.length + 1);
          }
        });
      }, typingSpeed);
    }
    
    return () => clearTimeout(timer);
  }, [typedText, isDeleting, textPhase, entered]);

  // Listen for audio playback error and update state
  useEffect(() => {
    const handleAudioError = (event) => {
      console.log('Audio error event received');
      setAudioEnabled(false);
    };
    
    window.addEventListener('audioError', handleAudioError);
    return () => {
      window.removeEventListener('audioError', handleAudioError);
    };
  }, []);
  
  // Apply entered class to body when state changes
  useEffect(() => {
    if (entered) {
      document.body.classList.add('entered');
      
      // Force all content elements to be transparent
      const contentElement = document.querySelector('.content');
      if (contentElement) {
        contentElement.style.backgroundColor = 'transparent';
        contentElement.style.background = 'none';
        contentElement.style.boxShadow = 'none';
        contentElement.style.border = 'none';
      }
    } else {
      document.body.classList.remove('entered');
    }
    
    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove('entered');
    };
  }, [entered]);

  const handleEnter = () => {
    // Update the entered state
    setEntered(true);
    setAudioEnabled(true);
    
    // Create and dispatch custom event to play video and audio
    const playEvent = new Event('playVideo');
    window.dispatchEvent(playEvent);
    
    // Also try to trigger interaction for audio playback
    setTimeout(() => {
      // This synthetic event might help with audio autoplay
      document.body.click();
    }, 100);
  };

  return (
    <div className={`landing-page ${entered ? 'entered' : ''}`} style={{ background: entered ? 'none' : '#000' }}>
      {!entered ? (
        <div className="enter-screen" onClick={handleEnter}>
          <div className="enter-container">
            <div className="typing-text">
              <span className="typed-content">{typedText}</span>
              <span className={`cursor ${typingComplete ? 'blink' : ''}`}>|</span>
            </div>
          </div>
        </div>
      ) : (
        <>
          <ProfileSection />
          {!audioEnabled && (
            <button 
              className="audio-fix-button"
              onClick={() => {
                const audio = document.querySelector('.background-audio');
                if (audio) audio.play().then(() => setAudioEnabled(true));
              }}
            >
              Click for audio
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default LandingPage; 